import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Coins,
  Download,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Explain } from '@/components/Explain';
import { cn } from '@/lib/cn';
import type { Bootstrap } from '@/lib/bootstrap';
import type { TimeZoneMode } from '@/lib/format';
import type { DailyReportInput, DailyReportSort } from '@/types';
import { ApiError, fetchClients } from '@/features/bet-inspection/lib/api';
import { ReportFilterBar, type ReportFilters } from '@/features/player-reports/components/ReportFilterBar';
import { fetchReportCurrencies } from '@/features/player-reports/lib/api';
import { defaultRange, toClickhouseUtc } from '@/features/player-reports/lib/time';
import { downloadCsv, financeFilename, toCsv } from './lib/csv';
import { fetchDailyReport } from './lib/api';

/**
 * Daily Finance Report — day-by-day stake, win and gross gaming revenue.
 *
 * The same ClickHouse rollups as Player Reports, grouped by day rather than by
 * player; the package shares one query builder between them so the two screens
 * can never disagree about revenue for the same window.
 *
 * ⚠️ Days are **UTC** days, always, regardless of the console's timezone
 * toggle. A finance day has to mean one fixed thing — bucketing by the reader's
 * timezone would move revenue between days depending on who opened the page.
 */
export function DailyReports({
  boot,
  tz,
  base,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  /** Global base currency — only used when no single currency is filtered. */
  base: string;
}) {
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [applied, setApplied] = useState<ReportFilters | null>(null);
  const [sort, setSort] = useState<DailyReportSort>('day');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const currencies = useQuery({
    queryKey: ['report-currencies', boot.api, filters.psid],
    queryFn: ({ signal }) =>
      fetchReportCurrencies({
        api: boot.api!,
        token: boot.token,
        playServiceId: filters.psid,
        signal,
      }),
    enabled: Boolean(boot.api && filters.psid),
    staleTime: 5 * 60_000,
  });

  // Sorting re-queries so the server always orders the full set.
  // `base` is part of the key: changing it re-queries — but only matters when no
  // single currency is filtered (the server ignores it otherwise).
  const report = useQuery({
    queryKey: ['daily-report', boot.api, applied, sort, dir, base],
    queryFn: ({ signal }) =>
      fetchDailyReport({
        api: boot.api!,
        token: boot.token,
        input: { ...toInput(applied!, base), sort, dir },
        signal,
      }),
    enabled: Boolean(boot.api && applied),
  });

  const rows = report.data?.rows ?? [];
  const totals = report.data?.totals;
  const unit = report.data?.unit ?? applied?.currency ?? base;

  const csv = useMemo(() => (rows.length ? toCsv(rows) : ''), [rows]);

  // Exports are named by the project's NAME, not its opaque play_service_id.
  const clientName = useMemo(
    () =>
      (applied &&
        (clients.data ?? []).find((c) => c.play_service_id === applied.psid)?.client_name) ||
      applied?.psid ||
      '',
    [clients.data, applied],
  );

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <ReportFilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={() => setApplied({ ...filters })}
          onReset={() => {
            setFilters(defaultFilters());
            setApplied(null);
          }}
          loading={report.isFetching}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
          currencies={currencies.data ?? []}
          tz={tz}
          base={base}
          // There is no per-player dimension on this screen.
          showPlayer={false}
        />
      </div>

      {report.isError && <Failure error={report.error as Error} />}
      {!applied && !report.isError && <Idle />}

      {applied && !report.isError && (
        <main className="rise">
          <KpiRow
            totals={totals}
            unit={unit}
            source={report.data?.source ?? 'settled'}
            converted={report.data?.converted ?? false}
            days={rows.length}
            loading={report.isFetching}
          />

          {/* Converted figures exclude any currency we hold no rate for — the
              reader has to know the total is short, not just see a number. */}
          {report.data?.unconverted?.length ? (
            <div className="flex items-center gap-2 border-b border-hairline bg-warn-bg/40 px-4 py-1.5 text-[12px] text-text-dim">
              <span className="text-warn">▲</span>
              Excluded from these totals — no exchange rate:{' '}
              <span className="font-mono text-warn">{report.data.unconverted.join(', ')}</span>
            </div>
          ) : null}

          <section className="border-b border-hairline">
            <div className="flex items-center gap-3 px-4 py-2">
              <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
                Daily Finance
              </span>
              <span className="font-mono text-[11px] text-text-dim">
                {report.isFetching
                  ? 'loading…'
                  : `${rows.length} day${rows.length === 1 ? '' : 's'} with activity`}
              </span>
              <button
                type="button"
                disabled={!csv}
                onClick={() =>
                  downloadCsv(
                    financeFilename(
                      clientName,
                      unit,
                      toClickhouseUtc(applied.from),
                      toClickhouseUtc(applied.to),
                    ),
                    csv,
                  )
                }
                className="ml-auto inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text disabled:opacity-40"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse whitespace-nowrap text-[12px]">
                <thead>
                  <tr className="border-b border-hairline bg-surface-sunken">
                    <Th col="day" sort={sort} dir={dir} onSort={setSort} onDir={setDir} align="left">
                      Date (UTC)
                    </Th>
                    <Th col="bet" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Bet</Th>
                    <Th col="win" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Win</Th>
                    {/* No server sort key for rollback — the header stays inert
                        rather than pretending to sort. */}
                    <Th col="ggr" sort={sort} dir={dir} onSort={setSort} onDir={setDir} nonSortable>
                      Rollback
                    </Th>
                    <Th col="ggr" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>GGR</Th>
                    <Th col="hold" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Hold %</Th>
                    <Th col="players" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Players</Th>
                    <Th col="bets" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Bets</Th>
                    <Th col="rounds" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Rounds</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.day}
                      className="border-b border-hairline/50 last:border-0 hover:bg-surface-raised/50"
                    >
                      <td className="tnum px-3 py-1.5 font-mono text-text">{r.day}</td>
                      <td className="px-3 py-1.5 text-right"><Amount value={r.bet} /></td>
                      <td className="px-3 py-1.5 text-right"><Amount value={r.win} /></td>
                      <td className="px-3 py-1.5 text-right">
                        <Amount value={r.rollback_amount} tone={Number(r.rollback_amount) > 0 ? 'warn' : undefined} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Amount value={r.ggr} tone={Number(r.ggr) < 0 ? 'warn' : undefined} />
                      </td>
                      <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">
                        {formatPct(r.hold)}
                      </td>
                      <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">
                        {r.players}
                      </td>
                      <td className="tnum px-3 py-1.5 text-right font-mono text-text">{r.bets}</td>
                      <td className="tnum px-3 py-1.5 text-right font-mono text-text">{r.rounds}</td>
                    </tr>
                  ))}
                  {!rows.length && !report.isFetching && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-center text-[12px] text-text-dim">
                        No activity for this project, currency and period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="px-4 py-6 text-center text-[11px] leading-relaxed text-text-faint">
            ggr = bet − win − rollback · hold = ggr / (bet − rollback) · days are UTC
            <br />
            The players column is distinct players <em>on that day</em> — summing it double-counts
            anyone who played on more than one.
          </footer>
        </main>
      )}
    </>
  );
}

function KpiRow({
  totals,
  unit,
  source,
  converted,
  days,
  loading,
}: {
  totals?: {
    players: number;
    bet: string;
    win: string;
    turnover: string;
    ggr: string;
    hold: number;
    bets: number;
    rounds: number;
  };
  unit: string;
  source: 'settled' | 'live';
  converted: boolean;
  days: number;
  loading: boolean;
}) {
  return (
    <section className="border-b border-hairline bg-surface">
      <div className="flex flex-wrap items-stretch divide-x divide-hairline">
        <Kpi
          label="Total Bet"
          icon={<Coins size={12} />}
          value={totals ? formatAmount(totals.bet) : '—'}
          sub={totals ? `${totals.rounds} rounds · ${totals.bets} bets` : ''}
          currency={unit}
          loading={loading}
        />
        <Kpi
          label="Total Win"
          value={totals ? formatAmount(totals.win) : '—'}
          sub="player payouts"
          currency={unit}
          loading={loading}
        />
        <Kpi
          label="GGR"
          icon={<TrendingUp size={12} />}
          value={totals ? formatAmount(totals.ggr) : '—'}
          // The mock said "Bet − Win"; the real model subtracts rollbacks too,
          // and the label has to state what the number actually is.
          sub="bet − win − rollback"
          currency={unit}
          loading={loading}
        />
        <Kpi
          label="Hold"
          icon={<Users size={12} />}
          value={totals ? formatPct(totals.hold) : '—'}
          sub="ggr / turnover"
          loading={loading}
        />
        <Kpi
          label="Players"
          value={totals ? String(totals.players) : '—'}
          sub={`over ${days} day${days === 1 ? '' : 's'}`}
          loading={loading}
        />

        <div className="ml-auto flex items-center gap-1.5 px-4">
          {converted && (
            <span
              title={`All currencies converted to ${unit} using the platform's exchange rates — approximate`}
              className="inline-flex items-center border border-info/40 bg-info-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-info"
            >
              fx → {unit}
            </span>
          )}
          <span
            title={
              source === 'live'
                ? 'Realtime mirror — current, but only successfully-processed transactions'
                : 'Settled rollup — authoritative, about an hour behind'
            }
            className={cn(
              'inline-flex items-center gap-1.5 border px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider',
              source === 'live'
                ? 'border-warn/40 bg-warn-bg text-warn'
                : 'border-ok/40 bg-ok-bg text-ok',
            )}
          >
            {source}
          </span>
        </div>
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
  currency,
  icon,
  loading,
}: {
  label: string;
  value: string;
  sub?: string;
  currency?: string;
  icon?: ReactNode;
  loading: boolean;
}) {
  return (
    <div className="min-w-[180px] px-4 py-2.5">
      <div className="eyebrow mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          'tnum font-mono text-[19px] font-semibold text-text',
          loading && 'opacity-50',
        )}
      >
        {value}
        {currency && value !== '—' && (
          <span className="ml-1 text-[11px] font-normal text-text-faint">{currency}</span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  );
}

function Th({
  col,
  sort,
  dir,
  onSort,
  onDir,
  align = 'right',
  children,
  nonSortable,
}: {
  col: DailyReportSort;
  sort: DailyReportSort;
  dir: 'asc' | 'desc';
  onSort: (c: DailyReportSort) => void;
  onDir: (d: 'asc' | 'desc') => void;
  align?: 'left' | 'right';
  children: ReactNode;
  /** Columns the server has no sort key for stay inert rather than lying. */
  nonSortable?: boolean;
}) {
  const active = !nonSortable && sort === col;
  if (nonSortable) {
    return (
      <th className={cn('px-3 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
        <span className="eyebrow text-text-dim">{children}</span>
      </th>
    );
  }
  return (
    <th className={cn('px-3 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => {
          if (active) onDir(dir === 'asc' ? 'desc' : 'asc');
          else {
            onSort(col);
            onDir('desc');
          }
        }}
        className={cn(
          'eyebrow inline-flex items-center gap-1 hover:text-text',
          active ? 'text-accent' : 'text-text-dim',
        )}
      >
        {children}
        {active ? (
          dir === 'asc' ? (
            <ArrowUp size={10} />
          ) : (
            <ArrowDown size={10} />
          )
        ) : (
          <span className="w-[10px]" />
        )}
      </button>
    </th>
  );
}

/** 2dp for reading, the exact stored value on hover — never destroy precision. */
function Amount({ value, tone }: { value: string; tone?: 'warn' }) {
  const n = Number(value);
  return (
    <span
      title={value}
      className={cn('tnum cursor-help font-mono', tone === 'warn' ? 'text-warn' : 'text-text')}
    >
      {Number.isFinite(n) ? formatAmount(value) : value}
    </span>
  );
}

function formatAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPct(hold: number): string {
  if (!Number.isFinite(hold)) return '—';
  return `${(hold * 100).toFixed(2)}%`;
}

/**
 * The query is always UTC. `currency` is only sent when one is chosen — omitting
 * it is what asks for all-currency, FX-converted figures. `base` is only sent in
 * that case, so a single-currency report is unaffected by the global base.
 */
function toInput(f: ReportFilters, base: string): DailyReportInput {
  return {
    play_service_id: f.psid,
    ...(f.currency ? { currency: f.currency } : { base }),
    from: toClickhouseUtc(f.from),
    to: toClickhouseUtc(f.to),
    ...(f.live ? { live: true } : {}),
  };
}

/** Default window: start of the current UTC month → the current hour. */
function defaultFilters(): ReportFilters {
  const { from, to } = defaultRange();
  return { psid: '', currency: '', from, to, playerId: '', live: false };
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <CalendarDays size={22} className="text-text-faint" />
      <p className="max-w-[480px] text-[13px] leading-relaxed text-text-dim">
        Day-by-day stake, win and{' '}
        <Explain term="ggr">gross gaming revenue</Explain> for a project. Pick a project, currency
        and period to begin.
      </p>
      <p className="max-w-[480px] text-[12px] leading-relaxed text-text-faint">
        Days are UTC. The settled rollup runs about an hour behind, so today's row keeps growing;
        the live toggle reads the realtime mirror instead.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Report failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
